import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ECB_HIST_90D_URL, parseHistoricalSeries, currencyName, isValidCurrencyCode } from '../lib/ecbFx';
import './FinanceTicker.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

/**
 * Shows the last ~90 calendar days of the EUR/<currency> reference rate.
 *
 * We use eurofxref-hist-90d.xml rather than the full eurofxref-hist.xml
 * (history back to 1999) on purpose: the full file is several MB and ~9,500
 * day-cubes for every currency, which is a lot to ship to a browser tab for
 * a "recent trend" widget. If you want a deeper "view full history" option
 * later, the parsing helper (parseHistoricalSeries in src/lib/ecbFx.js)
 * already works against eurofxref-hist.xml too — just point ECB_HIST_90D_URL
 * at it, ideally behind a "load full history" click rather than on mount.
 */
export default function CurrencyHistoryChart({ currency }) {
  const [series, setSeries] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error | invalid

  useEffect(() => {
    if (!isValidCurrencyCode(currency)) {
      setStatus('invalid');
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    setStatus('loading');

    fetch(ECB_HIST_90D_URL, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`ECB feed responded ${res.status}`);
        return res.text();
      })
      .then((xml) => {
        const parsed = parseHistoricalSeries(xml, currency);
        if (!parsed.length) {
          setStatus('error');
          return;
        }
        setSeries(parsed);
        setStatus('ready');
      })
      .catch((err) => {
        console.warn('Currency history unavailable:', err.message);
        setStatus('error');
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [currency]);

  if (status === 'invalid') {
    return (
      <div className='currency-history'>
        <p>"{currency}" isn't a recognized currency code.</p>
        <Link to='/finances' className='post-link'>← Back to all rates</Link>
      </div>
    );
  }

  const name = currencyName(currency);
  const latest = series[series.length - 1];
  const first = series[0];
  const change = latest && first ? (((latest.rate - first.rate) / first.rate) * 100).toFixed(2) : null;

  const chartData = {
    labels: series.map((p) => p.date),
    datasets: [
      {
        label: `EUR/${currency}`,
        data: series.map((p) => p.rate),
        borderColor: '#06b6d4',
        backgroundColor: 'rgba(6, 182, 212, 0.1)',
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: false, ticks: { color: '#9ca3af' } },
      x: { ticks: { color: '#9ca3af', maxTicksLimit: 8 } },
    },
    plugins: {
      legend: { labels: { color: '#9ca3af' } },
      title: { display: true, text: `EUR/${currency} — last ${series.length} trading days`, color: '#fff' },
    },
  };

  return (
    <div className='currency-history'>
      <Link to='/finances' className='post-link'>← Back to all rates</Link>
      <h2 style={{ marginTop: '1rem' }}>EUR/{currency} — {name}</h2>

      {status === 'loading' && <p>Loading exchange rate history…</p>}
      {status === 'error' && (
        <p>
          History is temporarily unavailable from the European Central Bank feed.
          Please try again shortly.
        </p>
      )}

      {status === 'ready' && (
        <>
          <p>
            Latest: <strong>{latest.rate.toFixed(4)}</strong> on {latest.date}
            {change !== null && (
              <> &middot; {change >= 0 ? '+' : ''}{change}% over this period</>
            )}
          </p>
          <div style={{ height: '360px', margin: '1.5rem 0' }}>
            <Line data={chartData} options={chartOptions} />
          </div>

          {/* Real text/table data alongside the canvas chart — canvas content
              isn't crawlable or accessible on its own. */}
          <details>
            <summary>View as a table (last 10 days)</summary>
            <table className='currency-history__table'>
              <thead>
                <tr><th>Date</th><th>EUR/{currency}</th></tr>
              </thead>
              <tbody>
                {series.slice(-10).reverse().map((p) => (
                  <tr key={p.date}>
                    <td>{p.date}</td>
                    <td>{p.rate.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}
    </div>
  );
}
